// Must be the very first import: installs Promise.withResolvers/URL.parse
// polyfills before anything (including pdfjs-dist) can execute without them.
import './lib/polyfills';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import * as Sentry from '@sentry/react';
import { beforeSend } from './instrument-scrub';

// Fontsource — body font
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';

// Fontsource — display font
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';

// Global styles (imports tokens.css internally)
import './styles/global.css';

import App from './App';

// Wires the real update flow for the SW's registerType: 'autoUpdate'
// (vite.config.ts): workbox-window's 'activated' event only fires
// `event.isUpdate`/`isExternal` true when it REPLACES an existing
// controller — never on first install — so this reloads the tab once,
// exactly when a new deploy's SW takes over, not in a loop. Without this
// import, vite-plugin-pwa falls back to a bare `navigator.serviceWorker
// .register()` script with no update listener at all, which is what let
// the sprint-9 deploy go unseen behind a stale cached shell.
registerSW({ immediate: true });

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
    release: import.meta.env.VITE_GIT_SHA,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.05'),
    beforeSend,
  });
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found in the document.');
}

createRoot(container).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please reload the page.</p>}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
